import type { Vehicle, VehicleGroup } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import { useVehicle } from '@cobliteam/hooks/src';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import useVehicleOdometer from '@cobliteam/hooks/src/useEntity/useVehicleOdometer';
import { useTranslation } from '@cobliteam/i18n';
import { Banner, BannerContent, Dialog, Popover, Typography } from '@cobliteam/ui/components';
import { PopoverContent, PopoverIcon, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';
import { useEffect } from 'react';
import { getFuelOptions, getSizeOptions, getVehicleIconOptions } from '../VehicleDrawer/options';
import VehicleCardHeader from './VehicleCardHeader';
import VehicleCardItem, { type VehicleCardItemProps } from './VehicleCardItem';
import VehicleCardMap from './VehicleCardMap';
import VehicleDialogSkeleton from './VehicleDialogSkeleton';
import VehicleTagsDisplay from './VehicleTagsDisplay';

export interface VehicleDialogViewProps {
  vehicle: Vehicle;
  vehicleGroups: VehicleGroup[];
  onClose: () => void;
}

const VehicleDialogView: React.FC<VehicleDialogViewProps> = ({ vehicle: initialVehicle, vehicleGroups: _vehicleGroups, onClose }) => {
  const { t } = useTranslation('vehicles');
  const { t: tGlobalSearch } = useTranslation('global-search');
  const { setPosition } = useChatWidget();
  const { getVehicle } = useVehicle();

  const { data: fetchedVehicle, isLoading } = getVehicle(initialVehicle.id, { enabled: !!initialVehicle.id });
  const vehicle = fetchedVehicle || initialVehicle;

  const { getOdometer } = useVehicleOdometer();
  const { data: odometer = undefined, isPending: isLoadingOdometer = false } = vehicle.id ? getOdometer(vehicle.id) : { data: undefined, isPending: false };

  const { data: devices } = useGetDevices({
    options: {
      enabled: !!vehicle.cobliId,
    },
  });
  const device = devices?.find((d) => d.cobliId === vehicle.cobliId);

  const sizeOptions = getSizeOptions(t);
  const fuelOptions = getFuelOptions(t);
  const vehicleIconOptions = getVehicleIconOptions(t);

  useEffect(() => {
    setPosition('drawer');
    return () => {
      setPosition('default');
    };
  }, [setPosition]);

  const getDisplayValue = (value: string | undefined | null, fallback = '--') => {
    return value && value !== 'empty' && value !== 'null' ? value : fallback;
  };

  const getFuelTypeLabel = (fuelType?: string) => {
    if (!fuelType) return '--';
    const fuelOption = fuelOptions.find((option) => option.value === fuelType);
    return fuelOption?.label || fuelType;
  };

  const getSizeLabel = (size?: string) => {
    if (!size) return '--';
    const sizeOption = sizeOptions.find((option) => option.value === size);
    return sizeOption?.label || size;
  };

  const getVehicleIconLabel = (iconType?: string) => {
    if (!iconType) return '--';
    const iconOption = vehicleIconOptions.find((option) => option.value === iconType);
    return iconOption?.label || iconType;
  };

  const getVehicleGroupName = () => {
    if (!vehicle.groups || vehicle.groups.length === 0) return null;
    return vehicle.groups[0].name;
  };

  const formatOdometer = (odometerValue?: number) => {
    if (odometerValue === undefined || odometerValue === null) return '--';
    return new Intl.NumberFormat('pt-BR').format(odometerValue);
  };

  const formatFuelPrice = (price?: number) => {
    if (price === undefined || price === null) return '--';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const formatFuelConsumption = (consumption?: number) => {
    if (consumption === undefined || consumption === null) return '--';
    return `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(consumption)} km/L`;
  };

  const formatSpeedLimit = (speedLimit?: number) => {
    if (speedLimit === undefined || speedLimit === null) return t('buttons.no_speed_limit');
    return `${speedLimit} km/h`;
  };

  const formatDate = (timestamp?: number | string) => {
    if (!timestamp) return '--';
    return new Date(timestamp).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  if (isLoading) {
    return <VehicleDialogSkeleton />;
  }

  const currentDriver = vehicle.newDriver || vehicle.driver;
  const driverDisplayValue = getDisplayValue(currentDriver?.name);
  const vehicleGroupName = getVehicleGroupName();
  const vehicleModelInfo = [getDisplayValue(vehicle.model?.brand), getDisplayValue(vehicle.model?.name), getDisplayValue(vehicle.model?.year)]
    .filter((v) => v !== '--')
    .join(' - ');

  const cardDevice: VehicleCardItemProps = {
    disabled: !vehicle.cobliId,
    title: vehicle.cobliId ? `${vehicle.cobliId}` : tGlobalSearch('vehicle_details.no_device_associated'),
    tooltip: vehicle.cobliId ? tGlobalSearch('vehicle_details.associated_device') : tGlobalSearch('vehicle_details.no_device_associated'),
    subtitle: 'Status: Normal',
    icon: 'router',
    color: 'accessory-purple',
    itemLink: `${Location.DEVICES}?deviceAssociation=${vehicle.cobliId ? 'associated' : 'not_associated'}&search=${vehicle.cobliId}`,
    pageLink: `${Location.VEHICLES}?search=${vehicle.licensePlate}`,
    onLinkClick: onClose,
  };

  const cardDriver: VehicleCardItemProps = {
    disabled: !currentDriver?.id,
    title: driverDisplayValue !== '--' ? driverDisplayValue : tGlobalSearch('vehicle_details.no_driver_associated'),
    tooltip: currentDriver?.id ? tGlobalSearch('vehicle_details.associated_driver') : tGlobalSearch('vehicle_details.no_driver_associated'),
    subtitle: t('labels.driver'),
    icon: 'user',
    color: 'warning',
    itemLink: `${Location.DRIVER_MANAGEMENT}?search=${currentDriver?.id}`,
    pageLink: `${Location.VEHICLES}?search=${vehicle.licensePlate}&tab=${currentDriver?.id ? 'Associado' : 'Desassociado'}`,
    onLinkClick: onClose,
  };

  const cardGroups: VehicleCardItemProps = {
    disabled: !vehicle.groups?.length,
    title: vehicleGroupName || tGlobalSearch('vehicle_group_details.no_group_associated'),
    tooltip: vehicle.groups?.length ? tGlobalSearch('vehicle_details.associated_group') : tGlobalSearch('vehicle_details.no_group_associated'),
    icon: 'cb-filters-vehicle-group-cb',
    color: 'success',
    itemLink: `${Location.VEHICLES_GROUPS}?search=${vehicle.groups?.[0]?.id}`,
    pageLink: `${Location.VEHICLES_GROUPS}`,
    onLinkClick: onClose,
  };

  // Odometer Card (custom)
  const odometerValue = odometer?.createdOdometerInKm || vehicle.odometer?.createdOdometerInKm;
  const cardOdometer: VehicleCardItemProps = {
    disabled: !(odometerValue || isLoadingOdometer),
    title: isLoadingOdometer ? t('status.loading_odometer_data') : `${formatOdometer(odometerValue)} km`,
    tooltip: t('labels.odometer'),
    subtitle: odometer?.odometerDateAt ? formatDate(odometer.odometerDateAt) : undefined,
    icon: 'gauge',
    color: 'primary',
    itemLink: '',
    pageLink: '',
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content size='lg' className='max-w-4xl'>
        <Dialog.Header className='w-full'>
          <VehicleCardHeader
            icon='truck'
            color='primary'
            title={getDisplayValue(vehicle.licensePlate)}
            subtitle={vehicleModelInfo || undefined}
            link={`${Location.VEHICLES}?search=${vehicle.licensePlate}`}
            linkTooltip={tGlobalSearch('vehicle_details.go_to_vehicles_page')}
            onLinkClick={onClose}
          />
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body className='flex max-h-[80vh] flex-col gap-6 overflow-y-auto p-6'>
          {/* Tags */}
          {vehicle.tags && vehicle.tags.length > 0 && (
            <div className='flex w-full flex-row flex-wrap items-center gap-2'>
              <div className='flex items-center justify-center rounded-md p-1'>
                <Icon name='tag' size='sm' className='text-gray-9' />
              </div>
              <VehicleTagsDisplay tags={vehicle.tags} />
            </div>
          )}

          {/* Details Grid */}
          <div className='grid grid-cols-12 gap-2'>
            <div className='col-span-5'>
              <div className='mb-1 flex items-center gap-2 font-medium text-sm'>
                <Icon name='fuel' className='!size-4' />
                {t('labels.fuel')}
              </div>
              <div className='ml-5 w-full'>
                <div className='text-sm'>
                  {tGlobalSearch('vehicle_details.type')} {getFuelTypeLabel(vehicle.fuel?.type)}
                </div>
                <div className='text-sm'>
                  {tGlobalSearch('vehicle_details.price')} {formatFuelPrice(vehicle.fuel?.price)}
                </div>
                <div className='text-sm'>
                  {tGlobalSearch('vehicle_details.consumption')} {formatFuelConsumption(vehicle.fuelConsumption)}
                </div>
              </div>
            </div>

            <div className='col-span-7'>
              <div className='mb-1 flex flex-1 items-center gap-2 font-medium text-sm'>
                <Icon name='receipt-text' className='!size-4' />
                {tGlobalSearch('vehicle_details.details')}
              </div>
              <div className='ml-5 w-full'>
                <div className='text-sm'>
                  {tGlobalSearch('vehicle_details.size')} {getSizeLabel(vehicle.size)}
                </div>
                <div className='flex items-center gap-1 text-sm'>
                  {tGlobalSearch('vehicle_details.speed_limit')}
                  <span>{formatSpeedLimit(vehicle.speedLimitInKmh)}</span>
                  {vehicle.speedLimitInKmh && (
                    <Popover>
                      <PopoverTrigger>
                        <PopoverIcon icon={<Icon name='circle-help' size='xs' className='!size-4' />} />
                      </PopoverTrigger>
                      <PopoverContent>
                        <Typography variant='description-title'>{t('status.speed_limit_alerts_title')}</Typography>
                        <Typography variant='description-list-default'>{t('status.speed_limit_alerts_description')}</Typography>
                        <ul>
                          <li>{t('status.monitoring_notification')}</li>
                          <li>{t('status.driver_cabin_alert')}</li>
                        </ul>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <div className='text-sm'>
                  {t('status.map_icon')}: {getVehicleIconLabel(vehicle.model?.type)}
                </div>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          <div className='grid grid-cols-2 gap-2'>
            <div className='flex flex-col gap-2'>
              <div>
                <Typography variant='text-md-medium'>Associações</Typography>
                <p className='text-gray-9 text-sm'>Veículo associado a dispositivos, motoristas e grupos</p>
              </div>
              <VehicleCardItem {...cardDevice} />
              <VehicleCardItem {...cardDriver} />
              <VehicleCardItem {...cardGroups} />
              <VehicleCardItem {...cardOdometer} />
            </div>
            <VehicleCardMap
              latitude={device?.lastLocation?.latitude}
              longitude={device?.lastLocation?.longitude}
              updatedAt={device?.lastLocation?.cobliIngestedAt}
              linkTo={`/monitoring?search=${vehicle.licensePlate}&focus=true`}
              onLinkClick={onClose}
            />
          </div>

          <Banner color='primary'>
            <BannerContent>
              <Typography variant='caption-default' className='text-bold text-primary-11'>
                {t('messages.warnings.driver_link_warning')}
              </Typography>
            </BannerContent>
          </Banner>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

export default VehicleDialogView;
